import { MonitorIcon, MoonIcon, SunIcon, type LucideIcon } from 'lucide-react';
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group.js';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip.js';
import { useTheme, type Theme } from './useTheme.js';

const OPTIONS: { value: Theme; label: string; Icon: LucideIcon }[] = [
  { value: 'light', label: 'Light', Icon: SunIcon },
  { value: 'system', label: 'Follow system', Icon: MonitorIcon },
  { value: 'dark', label: 'Dark', Icon: MoonIcon },
];

/**
 * Three icons rather than three words.
 *
 * The header is the densest strip in the console and LIGHT/SYSTEM/DARK spent a
 * lot of it on a control nobody touches twice a day. Sun, monitor and moon are
 * about as close to universally read as icons get, and each still carries its
 * word as an accessible name and as a tooltip — so the meaning is one hover
 * away and never colour- or shape-only.
 *
 * A `ToggleGroup type="single"` rather than the previous radio fieldset, to
 * match the range picker on Reports: Radix gives it `radiogroup`/`radio` roles
 * with `aria-checked`, roving arrow-key focus and a single tab stop, which the
 * hand-rolled fieldset did not have.
 */
export function ThemeToggle() {
  const { theme, setTheme } = useTheme();

  return (
    <ToggleGroup
      type="single"
      aria-label="Colour theme"
      value={theme}
      onValueChange={(next) => {
        // Radix clears the value when the active item is pressed again. There
        // is no "no theme", so an empty selection is ignored and the current
        // one stands.
        if (next === '') return;
        setTheme(next as Theme);
      }}
    >
      {OPTIONS.map(({ value, label, Icon }) => (
        <Tooltip key={value}>
          <TooltipTrigger asChild>
            <ToggleGroupItem
              value={value}
              aria-label={label}
              /* Square and equal-width, so the strip does not shift as the
                 active cell gains its weight. The accent rule underneath is
                 what makes the active one unmistakable rather than merely
                 tinted — the same treatment the range picker uses, keyed on
                 `aria-checked` because the tooltip on this trigger overwrites
                 the `data-state` the range picker can rely on.

                 `p-0` and `shrink-0` are not cosmetic: the shared toggle
                 variant carries the segmented control's own padding, and on a
                 cell fixed at 8 by 8 that padding left the flexible icon about
                 two pixels of width to shrink into. */
              className="h-full w-8 shrink-0 p-0 [&_svg]:shrink-0 aria-checked:shadow-[inset_0_-2px_0_0_var(--color-accent)]"
            >
              <Icon aria-hidden="true" className="size-4" />
            </ToggleGroupItem>
          </TooltipTrigger>
          <TooltipContent>{label}</TooltipContent>
        </Tooltip>
      ))}
    </ToggleGroup>
  );
}
